import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  Table,
  Tag,
  Button,
  Space,
  Tooltip,
  Progress,
  Alert,
  Segmented,
  Descriptions,
  Spin,
} from "antd";
import { MdClose, MdCloudUpload, MdRefresh, MdListAlt, MdErrorOutline, MdDownload, MdReplay } from "react-icons/md";
import * as XLSX from "xlsx";
import { consumoProveedorApi } from "../../services/api/consumoProveedorApi";

const FILTRO_ESTADO = {
  TODOS: "todos",
  PENDIENTES: "pendientes",
  EN_COLA: "en_cola",
  ENVIADOS: "enviados",
  ERRORES: "errores",
};

const RESUMEN_INICIAL = {
  total: 0,
  enviados_proveedor: 0,
  pendientes: 0,
  en_cola: 0,
  procesando: 0,
  errores: 0,
};

const extraerMensajeError = (error, fallback) => {
  const msg =
    error.response?.data?.detail ||
    error.response?.data?.message ||
    fallback;
  return typeof msg === "string" ? msg : JSON.stringify(msg);
};

const clasificarEstado = (consumo) => {
  if (consumo.enviado_proveedor) return FILTRO_ESTADO.ENVIADOS;
  if (consumo.estado_envio === "error") return FILTRO_ESTADO.ERRORES;
  if (
    consumo.estado_envio === "en_cola" ||
    consumo.estado_envio === "procesando" ||
    consumo.estado_envio === "reintento"
  ) {
    return FILTRO_ESTADO.EN_COLA;
  }
  return FILTRO_ESTADO.PENDIENTES;
};

const hayTrabajoEnCola = (res) =>
  (res?.en_cola || 0) + (res?.procesando || 0) > 0;

const truncarTexto = (texto, max = 120) => {
  if (!texto) return "—";
  const s = String(texto);
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

const ModalDetalleConsumosProveedor = ({
  turnoSeleccionado,
  isOpen,
  onClose,
  showToast,
}) => {
  const [loading, setLoading] = useState(false);
  const [enviandoId, setEnviandoId] = useState(null);
  const [enviandoTodos, setEnviandoTodos] = useState(false);
  const [progresoEnvio, setProgresoEnvio] = useState(null);
  const [erroresEnvio, setErroresEnvio] = useState([]);
  const [consumos, setConsumos] = useState([]);
  const [resumen, setResumen] = useState(RESUMEN_INICIAL);
  const [monitoreandoCola, setMonitoreandoCola] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState(FILTRO_ESTADO.TODOS);
  const [modalErrorOpen, setModalErrorOpen] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState(null);
  const [loadingErrorDetalle, setLoadingErrorDetalle] = useState(false);
  const [exportandoErrores, setExportandoErrores] = useState(false);

  const cargarDetalle = useCallback(async (silencioso = false) => {
    if (!turnoSeleccionado?.id) return null;
    if (!silencioso) setLoading(true);
    try {
      const { data } = await consumoProveedorApi.getDetalleTurno(turnoSeleccionado.id);
      const nuevoResumen = data.resumen || RESUMEN_INICIAL;
      setConsumos(data.data || []);
      setResumen(nuevoResumen);
      return nuevoResumen;
    } catch (error) {
      if (!silencioso) {
        showToast("error", extraerMensajeError(error, "Error al cargar detalle de consumos"));
      }
      return null;
    } finally {
      if (!silencioso) setLoading(false);
    }
  }, [turnoSeleccionado?.id, showToast]);

  useEffect(() => {
    if (!isOpen || !monitoreandoCola) return undefined;

    let pollsSinAvance = 0;
    let ultimoEnCola = null;

    const intervalId = setInterval(async () => {
      const nuevoResumen = await cargarDetalle(true);
      if (!nuevoResumen) return;

      const enColaAhora =
        (nuevoResumen.en_cola || 0) + (nuevoResumen.procesando || 0);

      // Avance del lote actual (no el total histórico del turno)
      setProgresoEnvio((prev) => {
        if (!prev || !prev.total) return prev;
        const enviados = nuevoResumen.enviados_proveedor || 0;
        const errores = nuevoResumen.errores || 0;
        const baseEnv = prev.baselineEnviados ?? 0;
        const baseErr = prev.baselineErrores ?? 0;
        const exitososLote = Math.max(0, enviados - baseEnv);
        const erroresLote = Math.max(0, errores - baseErr);
        // Preferir drenado de cola: lo que salió de "en cola" desde el inicio del lote
        const enColaInicial = prev.enColaInicial ?? prev.total;
        const porDrenado = Math.max(0, enColaInicial - enColaAhora);
        const porResultado = exitososLote + erroresLote;
        const procesados = Math.min(
          prev.total,
          Math.max(porDrenado, porResultado)
        );
        return {
          ...prev,
          procesados,
          exitosos: exitososLote,
          errores: erroresLote,
        };
      });

      if (!hayTrabajoEnCola(nuevoResumen)) {
        setMonitoreandoCola(false);
        setEnviandoTodos(false);
        setProgresoEnvio((prev) =>
          prev
            ? { ...prev, procesados: prev.total }
            : prev
        );
        if ((nuevoResumen.errores || 0) > 0) {
          showToast(
            "error",
            `Proceso finalizado: ${nuevoResumen.enviados_proveedor} enviado(s) en total, ${nuevoResumen.errores} error(es)`
          );
          setFiltroEstado(FILTRO_ESTADO.ERRORES);
        } else {
          showToast(
            "success",
            `Envío completado: ${nuevoResumen.enviados_proveedor} consumo(s) al proveedor`
          );
        }
        // Limpiar barra tras un momento breve (opcional: dejar null ya)
        setProgresoEnvio(null);
        return;
      }

      if (ultimoEnCola !== null && enColaAhora >= ultimoEnCola) {
        pollsSinAvance += 1;
      } else {
        pollsSinAvance = 0;
      }
      ultimoEnCola = enColaAhora;

      // ~90s sin bajar la cola → worker probablemente caído o jobs Redis perdidos
      if (pollsSinAvance >= 30) {
        setMonitoreandoCola(false);
        setEnviandoTodos(false);
        setProgresoEnvio(null);
        showToast(
          "warning",
          `La cola no avanza (${enColaAhora} en cola). Use "Reintentar en cola" o verifique el worker Redis en el servidor.`
        );
      }
    }, 3000);

    return () => clearInterval(intervalId);
  }, [isOpen, monitoreandoCola, cargarDetalle, showToast]);

  useEffect(() => {
    if (isOpen && turnoSeleccionado?.id) {
      cargarDetalle();
    }
    if (!isOpen) {
      setProgresoEnvio(null);
      setErroresEnvio([]);
      setMonitoreandoCola(false);
      setEnviandoTodos(false);
      setFiltroEstado(FILTRO_ESTADO.TODOS);
      setModalErrorOpen(false);
      setErrorDetalle(null);
    }
  }, [isOpen, turnoSeleccionado?.id, cargarDetalle]);

  const consumosFiltrados = useMemo(() => {
    if (filtroEstado === FILTRO_ESTADO.TODOS) return consumos;
    return consumos.filter((c) => clasificarEstado(c) === filtroEstado);
  }, [consumos, filtroEstado]);

  const consumosConError = useMemo(
    () => consumos.filter((c) => clasificarEstado(c) === FILTRO_ESTADO.ERRORES),
    [consumos]
  );

  const exportarErroresExcel = async () => {
    if (!consumosConError.length) {
      showToast("warning", "No hay consumos con error para exportar");
      return;
    }

    setExportandoErrores(true);
    try {
      const detalleConLogs = await Promise.all(
        consumosConError.map(async (consumo) => {
          try {
            const { data } = await consumoProveedorApi.getLogsEnvio(consumo.id_turno_detalle);
            const logs = data.data?.logs || [];
            const ultimoLog = logs.length ? logs[logs.length - 1] : null;
            return { consumo, sync: data.data?.sync || null, ultimoLog, logs };
          } catch {
            return { consumo, sync: null, ultimoLog: null, logs: [] };
          }
        })
      );

      const comedorNombreExport = turnoSeleccionado?.comedor?.nombre || "comedor";
      const servicioNombreExport =
        turnoSeleccionado?.tipo_servicio_alimentacion?.descripcion || "servicio";
      const fechaTurnoExport = turnoSeleccionado?.fecha_turno
        ? String(turnoSeleccionado.fecha_turno).split("T")[0]
        : "—";

      const filasDetalle = detalleConLogs.map(({ consumo, sync, ultimoLog }) => ({
        "ID turno detalle": consumo.id_turno_detalle,
        DNI: consumo.dni || "",
        "Doc. comedor": consumo.numero_documento_comedor || "",
        Comensal: consumo.comensal_nombre || "",
        Comedor: consumo.comedor || comedorNombreExport,
        Servicio: consumo.servicio || servicioNombreExport,
        "Tipo comida": consumo.tipo_comida || "",
        Transportado: consumo.transportado ? "true" : "false",
        "Fecha consumo": consumo.fecha || "",
        "Hora consumo": consumo.hora || "",
        "Fecha registro": consumo.fecha_registro || "",
        Cantidad: consumo.cantidad ?? 1,
        "Precio venta": consumo.precio_venta ?? "",
        Intentos: sync?.intentos ?? consumo.intentos ?? 0,
        "Estado sync": sync?.estado_envio || consumo.estado_envio || "error",
        "Fecha último envío": sync?.fecha_envio || consumo.fecha_envio || "",
        "HTTP último intento": ultimoLog?.http_status ?? "",
        "Estado último log": ultimoLog?.estado || "",
        "Fecha último intento": ultimoLog?.created_at || "",
        "Respuesta proveedor (sync)":
          sync?.respuesta_proveedor || consumo.respuesta_proveedor || "",
        "URL solicitud (último intento)": ultimoLog?.request_url || "",
        "Respuesta completa (último intento)": ultimoLog?.response_body || "",
      }));

      const filasResumen = [
        { Campo: "Comedor", Valor: comedorNombreExport },
        { Campo: "Servicio turno", Valor: servicioNombreExport },
        { Campo: "Fecha turno", Valor: fechaTurnoExport },
        { Campo: "ID turno cabecera", Valor: turnoSeleccionado?.id ?? "" },
        { Campo: "Total consumos turno", Valor: resumen.total },
        { Campo: "Total errores exportados", Valor: consumosConError.length },
        {
          Campo: "Generado el",
          Valor: new Date().toLocaleString("es-PE", { timeZone: "America/Lima" }),
        },
        {
          Campo: "Nota",
          Valor:
            "Detalle de consumos no sincronizados con Logiflex/Cumbrak para validación del proveedor.",
        },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filasResumen), "Resumen");
      const wsDetalle = XLSX.utils.json_to_sheet(filasDetalle);
      wsDetalle["!cols"] = [
        { wch: 14 },
        { wch: 12 },
        { wch: 14 },
        { wch: 28 },
        { wch: 22 },
        { wch: 14 },
        { wch: 10 },
        { wch: 12 },
        { wch: 10 },
        { wch: 18 },
        { wch: 8 },
        { wch: 10 },
        { wch: 8 },
        { wch: 12 },
        { wch: 18 },
        { wch: 10 },
        { wch: 14 },
        { wch: 18 },
        { wch: 40 },
        { wch: 50 },
        { wch: 50 },
      ];
      XLSX.utils.book_append_sheet(wb, wsDetalle, "Errores");

      const slugComedor = comedorNombreExport.replace(/[^\w]+/g, "_").slice(0, 30);
      const nombreArchivo = `Errores_Logiflex_${slugComedor}_${fechaTurnoExport}_${Date.now()}.xlsx`;
      XLSX.writeFile(wb, nombreArchivo);

      showToast(
        "success",
        `Excel exportado: ${consumosConError.length} error(es) para enviar al proveedor`
      );
    } catch (error) {
      showToast("error", extraerMensajeError(error, "Error al exportar errores a Excel"));
    } finally {
      setExportandoErrores(false);
    }
  };

  const abrirDetalleError = async (record) => {
    setModalErrorOpen(true);
    setErrorDetalle({
      record,
      sync: null,
      logs: [],
    });
    setLoadingErrorDetalle(true);
    try {
      const { data } = await consumoProveedorApi.getLogsEnvio(record.id_turno_detalle);
      setErrorDetalle({
        record,
        sync: data.data?.sync || null,
        logs: data.data?.logs || [],
      });
    } catch (error) {
      showToast("error", extraerMensajeError(error, "Error al cargar detalle del envío"));
      setErrorDetalle({
        record,
        sync: record.respuesta_proveedor
          ? { respuesta_proveedor: record.respuesta_proveedor, intentos: record.intentos }
          : null,
        logs: [],
      });
    } finally {
      setLoadingErrorDetalle(false);
    }
  };

  const colaAtascada =
    (resumen.en_cola || 0) + (resumen.procesando || 0) > 0;
  const cantidadEnCola =
    (resumen.en_cola || 0) + (resumen.procesando || 0);
  const cantidadErrores = resumen.errores || 0;
  const puedeEnviarPendientes = (resumen.pendientes || 0) > 0;
  const puedeReenviarErrores = cantidadErrores > 0;

  const iniciarProgresoLote = (totalLote) => {
    const total = Math.max(0, Number(totalLote) || 0);
    setProgresoEnvio({
      total,
      procesados: 0,
      exitosos: 0,
      errores: 0,
      baselineEnviados: resumen.enviados_proveedor || 0,
      baselineErrores: resumen.errores || 0,
      enColaInicial: total,
      actual: null,
    });
  };

  const enviarConsumo = async (record, forzarReenvio = false) => {
    setEnviandoId(record.id_turno_detalle);
    try {
      const { data } = await consumoProveedorApi.encolarConsumo(record.id_turno_detalle, {
        forzarReenvio,
        origen: "popup",
      });
      showToast("success", data.message || "Consumo encolado para envío al proveedor");
      iniciarProgresoLote(1);
      setMonitoreandoCola(true);
      await cargarDetalle(true);
    } catch (error) {
      showToast("error", extraerMensajeError(error, "Error al encolar consumo al proveedor"));
    } finally {
      setEnviandoId(null);
    }
  };

  const enviarPendientes = async () => {
    const pendientes = consumos.filter(
      (c) =>
        !c.enviado_proveedor &&
        c.estado_envio !== "error" &&
        c.estado_envio !== "en_cola" &&
        c.estado_envio !== "procesando" &&
        c.estado_envio !== "reintento"
    );
    if (!pendientes.length) return;

    setEnviandoTodos(true);
    setErroresEnvio([]);
    iniciarProgresoLote(pendientes.length);

    try {
      const { data } = await consumoProveedorApi.encolarPendientesTurno(turnoSeleccionado.id);
      const encolados = data.data?.encolados || pendientes.length;
      setProgresoEnvio((prev) =>
        prev
          ? { ...prev, total: encolados, enColaInicial: encolados }
          : prev
      );
      showToast(
        "success",
        data.message ||
          `${encolados} consumo(s) encolado(s). El worker los enviará al proveedor.`
      );
      setMonitoreandoCola(true);
      await cargarDetalle(true);
    } catch (error) {
      showToast("error", extraerMensajeError(error, "Error al encolar consumos al proveedor"));
      setEnviandoTodos(false);
      setProgresoEnvio(null);
    }
  };

  const ejecutarReintentarCola = async () => {
    setEnviandoTodos(true);
    setErroresEnvio([]);
    iniciarProgresoLote(cantidadEnCola);

    try {
      const { data } = await consumoProveedorApi.reencolarColaTurno(turnoSeleccionado.id);
      const encolados = data.data?.encolados || 0;
      setProgresoEnvio((prev) =>
        prev
          ? { ...prev, total: encolados, enColaInicial: encolados, procesados: 0 }
          : prev
      );
      showToast(
        "success",
        data.message ||
          `${encolados} consumo(s) reencolado(s). El worker los enviará al proveedor.`
      );
      if (encolados === 0) {
        showToast("warning", "No había consumos en cola para reencolar en este turno.");
        setEnviandoTodos(false);
        setProgresoEnvio(null);
      } else {
        setMonitoreandoCola(true);
      }
      await cargarDetalle(true);
    } catch (error) {
      showToast("error", extraerMensajeError(error, "Error al reencolar consumos en cola"));
      setEnviandoTodos(false);
      setProgresoEnvio(null);
    }
  };

  const reintentarCola = () => {
    if (!cantidadEnCola) return;

    Modal.confirm({
      title: "Reintentar consumos en cola",
      content: (
        <div>
          <p className="mb-2">
            Se reencolarán <strong>{cantidadEnCola}</strong> consumo(s) marcados
            &quot;En cola&quot; / &quot;Procesando&quot; / &quot;Reintento&quot;
            (incluye días anteriores si este turno quedó congelado).
          </p>
          <p className="mb-0 text-muted">
            Esto vuelve a meter los jobs en Redis. Confirme que el worker esté activo.
          </p>
        </div>
      ),
      okText: "Reintentar en cola",
      cancelText: "Cancelar",
      okButtonProps: { className: "btn-dh-primary" },
      cancelButtonProps: { className: "btn-dh-secondary" },
      centered: true,
      zIndex: 1100,
      onOk: () => ejecutarReintentarCola(),
    });
  };

  const ejecutarReenviarErrores = async () => {
    setEnviandoTodos(true);
    setErroresEnvio([]);
    iniciarProgresoLote(cantidadErrores);

    try {
      const { data } = await consumoProveedorApi.reenviarErroresTurno(turnoSeleccionado.id);
      const encolados = data.data?.encolados || 0;
      setProgresoEnvio((prev) =>
        prev
          ? { ...prev, total: encolados, enColaInicial: encolados, procesados: 0 }
          : prev
      );
      showToast(
        "success",
        data.message ||
          `${encolados} error(es) reencolado(s). El worker los reenviará al proveedor.`
      );
      if (encolados === 0) {
        showToast("warning", "No había consumos con error para reenviar en este turno.");
        setEnviandoTodos(false);
        setProgresoEnvio(null);
      } else {
        setMonitoreandoCola(true);
        setFiltroEstado(FILTRO_ESTADO.EN_COLA);
      }
      await cargarDetalle(true);
    } catch (error) {
      showToast("error", extraerMensajeError(error, "Error al reenviar consumos con error"));
      setEnviandoTodos(false);
      setProgresoEnvio(null);
    }
  };

  const reenviarErrores = () => {
    if (!puedeReenviarErrores) return;

    Modal.confirm({
      title: "Reenviar consumos con error",
      content: (
        <div>
          <p className="mb-2">
            Se reencolarán <strong>{cantidadErrores}</strong> consumo(s) con estado{" "}
            <strong>Error</strong> para reintento al proveedor.
          </p>
          <p className="mb-0 text-muted">
            Confirme que el worker Redis esté activo.
          </p>
        </div>
      ),
      okText: "Reenviar errores",
      cancelText: "Cancelar",
      okButtonProps: { className: "btn-dh-primary", danger: false },
      cancelButtonProps: { className: "btn-dh-secondary" },
      centered: true,
      zIndex: 1100,
      onOk: () => ejecutarReenviarErrores(),
    });
  };

  const porcentajeProgreso =
    progresoEnvio && progresoEnvio.total > 0
      ? Math.min(100, Math.round((progresoEnvio.procesados / progresoEnvio.total) * 100))
      : 0;

  const columns = [
    {
      title: "DNI / Doc.",
      dataIndex: "dni",
      key: "dni",
      width: 110,
      render: (_, r) => r.dni || r.numero_documento_comedor || "—",
    },
    {
      title: "Comensal",
      dataIndex: "comensal_nombre",
      key: "comensal_nombre",
      ellipsis: true,
    },
    {
      title: "Servicio",
      dataIndex: "servicio",
      key: "servicio",
      width: 120,
    },
    {
      title: "Tipo",
      dataIndex: "tipo_comida",
      key: "tipo_comida",
      width: 60,
      render: (v) => v || "—",
    },
    {
      title: "Transp.",
      dataIndex: "transportado",
      key: "transportado",
      width: 70,
      render: (v) => (v ? <Tag color="blue">Sí</Tag> : "No"),
    },
    {
      title: "Fecha",
      dataIndex: "fecha",
      key: "fecha",
      width: 105,
    },
    {
      title: "Hora",
      dataIndex: "hora",
      key: "hora",
      width: 70,
    },
    {
      title: "Estado proveedor",
      key: "enviado_proveedor",
      width: 150,
      render: (_, record) => {
        if (record.enviado_proveedor) {
          return (
            <Tooltip title={record.fecha_envio ? `Enviado: ${record.fecha_envio}` : ""}>
              <Tag color="success">Enviado</Tag>
            </Tooltip>
          );
        }
        if (record.estado_envio === "error") {
          return (
            <Tooltip title={truncarTexto(record.respuesta_proveedor, 200)}>
              <Tag color="error">Error</Tag>
            </Tooltip>
          );
        }
        if (record.estado_envio === "procesando") {
          return <Tag color="processing">Procesando</Tag>;
        }
        if (record.estado_envio === "reintento") {
          return (
            <Tooltip title={truncarTexto(record.respuesta_proveedor, 200) || "Esperando reintento automático"}>
              <Tag color="orange">Reintento {record.intentos || 0}</Tag>
            </Tooltip>
          );
        }
        if (record.estado_envio === "en_cola") {
          return <Tag color="blue">En cola</Tag>;
        }
        return <Tag color="default">Pendiente</Tag>;
      },
    },
    {
      title: "Intentos",
      dataIndex: "intentos",
      key: "intentos",
      width: 80,
      align: "center",
      render: (v) => v || 0,
    },
    {
      title: "Acción",
      key: "accion",
      width: 200,
      fixed: "right",
      render: (_, record) => (
        <Space size="small" wrap>
          {(record.estado_envio === "error" ||
            record.estado_envio === "reintento" ||
            (record.intentos > 0 && record.respuesta_proveedor)) && (
            <Button
              size="small"
              icon={<MdErrorOutline />}
              className="btn-dh-secondary"
              disabled={enviandoTodos}
              onClick={() => abrirDetalleError(record)}
            >
              Ver error
            </Button>
          )}
          {!record.enviado_proveedor ? (
            <Button
              type="primary"
              size="small"
              className="btn-dh-primary"
              loading={enviandoId === record.id_turno_detalle}
              disabled={enviandoTodos}
              onClick={() =>
                enviarConsumo(
                  record,
                  // Forzar reencolado si ya está en cola/error (cola huérfana Redis)
                  ["error", "reintento", "en_cola", "procesando"].includes(
                    record.estado_envio
                  )
                )
              }
            >
              {record.estado_envio === "error" ||
              record.estado_envio === "reintento" ||
              record.estado_envio === "en_cola" ||
              record.estado_envio === "procesando"
                ? "Reintentar"
                : "Consumir"}
            </Button>
          ) : (
            <Button
              size="small"
              className="btn-dh-secondary"
              loading={enviandoId === record.id_turno_detalle}
              disabled={enviandoTodos}
              onClick={() => enviarConsumo(record, true)}
            >
              Reenviar
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const comedorNombre = turnoSeleccionado?.comedor?.nombre || "—";
  const servicioNombre =
    turnoSeleccionado?.tipo_servicio_alimentacion?.descripcion || "—";

  const logColumns = [
    {
      title: "Fecha",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      width: 90,
      render: (estado) => (
        <Tag color={estado === "exitoso" ? "success" : "error"}>{estado}</Tag>
      ),
    },
    {
      title: "HTTP",
      dataIndex: "http_status",
      key: "http_status",
      width: 70,
      render: (v) => v ?? "—",
    },
    {
      title: "Respuesta proveedor",
      dataIndex: "response_body",
      key: "response_body",
      ellipsis: true,
      render: (v) => truncarTexto(v, 200),
    },
  ];

  return (
    <>
      <Modal
        title={
          <span className="d-flex align-items-center gap-2">
            <MdListAlt size={22} />
            Detalle de consumos — {comedorNombre}
          </span>
        }
        open={isOpen}
        onCancel={onClose}
        width={1020}
        footer={null}
        destroyOnClose
        className="modal-dh"
        closable={!enviandoTodos}
        maskClosable={!enviandoTodos}
      >
        <style>
          {`
          .modal-detalle-consumos .resumen-bar {
            background: linear-gradient(135deg, #f8f9fc 0%, #eef1f8 100%);
            border-radius: 10px;
            padding: 12px 16px;
            margin-bottom: 16px;
            border: 1px solid #e2e8f0;
          }
          .modal-detalle-consumos .resumen-bar strong { color: #667eea; }
          .modal-detalle-consumos .progreso-envio-panel {
            background: #fff;
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            padding: 14px 16px;
            margin-bottom: 16px;
          }
          .modal-detalle-consumos .progreso-envio-stats {
            display: flex;
            flex-wrap: wrap;
            gap: 12px 20px;
            margin-top: 10px;
            font-size: 0.875rem;
          }
          .modal-detalle-consumos .progreso-envio-stats .stat-exito { color: #059669; font-weight: 600; }
          .modal-detalle-consumos .progreso-envio-stats .stat-error { color: #dc2626; font-weight: 600; }
          .modal-detalle-consumos .progreso-envio-stats .stat-pendiente { color: #667eea; font-weight: 600; }
          .modal-detalle-consumos .filtro-estado-bar {
            margin-bottom: 12px;
          }
          .modal-detalle-consumos .log-url-box {
            background: #f8f9fc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 8px 10px;
            font-size: 0.75rem;
            word-break: break-all;
            max-height: 80px;
            overflow-y: auto;
          }
          .modal-detalle-consumos .log-response-box {
            background: #fff5f5;
            border: 1px solid #fecaca;
            border-radius: 8px;
            padding: 10px 12px;
            font-size: 0.8rem;
            white-space: pre-wrap;
            word-break: break-word;
            max-height: 160px;
            overflow-y: auto;
          }
        `}
        </style>

        <div className="modal-detalle-consumos">
          <div className="resumen-bar d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <div className="small text-muted">Servicio: {servicioNombre}</div>
              <div>
                Total: <strong>{resumen.total}</strong> · Enviados:{" "}
                <strong>{resumen.enviados_proveedor}</strong> · Pendientes:{" "}
                <strong>{resumen.pendientes}</strong> · En cola:{" "}
                <strong>{(resumen.en_cola || 0) + (resumen.procesando || 0)}</strong> · Errores:{" "}
                <strong style={{ color: resumen.errores > 0 ? "#dc2626" : undefined }}>
                  {resumen.errores || 0}
                </strong>
              </div>
            </div>
            <Space wrap>
              <Button
                icon={<MdDownload />}
                onClick={exportarErroresExcel}
                loading={exportandoErrores}
                disabled={
                  !consumosConError.length || enviandoTodos || exportandoErrores
                }
                className="btn-dh-secondary"
                title={
                  consumosConError.length
                    ? "Exportar detalle de errores para el proveedor"
                    : "No hay errores para exportar"
                }
              >
                Exportar errores Excel
              </Button>
              <Button
                icon={<MdRefresh />}
                onClick={cargarDetalle}
                loading={loading}
                disabled={enviandoTodos}
                className="btn-dh-secondary"
              >
                Actualizar
              </Button>
              <Button
                type="primary"
                icon={<MdCloudUpload />}
                className="btn-dh-primary"
                loading={enviandoTodos}
                disabled={!puedeEnviarPendientes || enviandoTodos}
                onClick={enviarPendientes}
                title="Encola solo consumos pendientes (aún no enviados ni en cola)"
              >
                {enviandoTodos || monitoreandoCola
                  ? progresoEnvio
                    ? `Procesando ${progresoEnvio.procesados}/${progresoEnvio.total}…`
                    : "Procesando cola…"
                  : "Enviar pendientes"}
              </Button>
              <Button
                icon={<MdReplay />}
                className="btn-dh-secondary"
                loading={enviandoTodos}
                disabled={!colaAtascada || enviandoTodos}
                onClick={reintentarCola}
                title="Fuerza reencolado Redis de consumos atrapados en cola (días anteriores incluidos)"
              >
                Reintentar en cola ({cantidadEnCola})
              </Button>
              <Button
                icon={<MdErrorOutline />}
                className="btn-dh-secondary"
                loading={enviandoTodos}
                disabled={!puedeReenviarErrores || enviandoTodos}
                onClick={reenviarErrores}
                title="Reencola todos los consumos con estado Error para reintento al proveedor"
              >
                Reenviar errores ({cantidadErrores})
              </Button>
              <Button
                icon={<MdClose />}
                onClick={onClose}
                disabled={enviandoTodos}
                className="btn-dh-secondary"
              >
                Cerrar
              </Button>
            </Space>
          </div>

          <div className="filtro-estado-bar">
            <Segmented
              value={filtroEstado}
              onChange={setFiltroEstado}
              disabled={enviandoTodos}
              options={[
                { label: `Todos (${resumen.total})`, value: FILTRO_ESTADO.TODOS },
                { label: `Pendientes (${resumen.pendientes})`, value: FILTRO_ESTADO.PENDIENTES },
                {
                  label: `En cola (${(resumen.en_cola || 0) + (resumen.procesando || 0)})`,
                  value: FILTRO_ESTADO.EN_COLA,
                },
                { label: `Enviados (${resumen.enviados_proveedor})`, value: FILTRO_ESTADO.ENVIADOS },
                {
                  label: `Errores (${resumen.errores || 0})`,
                  value: FILTRO_ESTADO.ERRORES,
                },
              ]}
            />
          </div>

          {(progresoEnvio || monitoreandoCola) && (
            <div className="progreso-envio-panel">
              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                <strong style={{ color: "#374151" }}>
                  Envío al proveedor en segundo plano (cola Redis)…
                </strong>
                <span className="text-muted small">
                  {progresoEnvio
                    ? `${progresoEnvio.procesados} de ${progresoEnvio.total} (${porcentajeProgreso}%)`
                    : "Actualizando estado…"}
                </span>
              </div>
              {progresoEnvio ? (
                <>
                  <Progress
                    percent={porcentajeProgreso}
                    status={progresoEnvio.errores > 0 ? "exception" : "active"}
                    strokeColor={{ from: "#667eea", to: "#764ba2" }}
                  />
                  <div className="progreso-envio-stats">
                    <span className="stat-exito">Exitosos: {progresoEnvio.exitosos}</span>
                    <span className="stat-error">Errores: {progresoEnvio.errores}</span>
                    <span className="stat-pendiente">
                      En cola: {(resumen.en_cola || 0) + (resumen.procesando || 0)}
                    </span>
                  </div>
                </>
              ) : (
                <Progress percent={100} status="active" showInfo={false} />
              )}
            </div>
          )}

          {!progresoEnvio && erroresEnvio.length > 0 && (
            <Alert
              type="warning"
              showIcon
              className="mb-3"
              message={`${erroresEnvio.length} consumo(s) con error en el último envío`}
              description={
                <ul className="mb-0 ps-3 small">
                  {erroresEnvio.slice(0, 5).map((e) => (
                    <li key={e.id_turno_detalle}>
                      <strong>{e.comensal}:</strong> {e.mensaje}
                    </li>
                  ))}
                  {erroresEnvio.length > 5 && (
                    <li>… y {erroresEnvio.length - 5} error(es) más</li>
                  )}
                </ul>
              }
            />
          )}

          <div className="dh-alert dh-alert-info mb-3 small">
            Los consumos se encolan y el worker los envía a Logiflex/Cumbrak (endpoint productivo).
            Puede cerrar este popup; el procesamiento continúa en el servidor. Estado{" "}
            <strong>Reintento N</strong>: falló un intento y se reintentará automáticamente (30s, 2min, 5min).
            Use <strong>Ver error</strong> para ver la respuesta del proveedor aunque siga en cola.
          </div>

          <Table
            rowKey="id_turno_detalle"
            columns={columns}
            dataSource={consumosFiltrados}
            loading={loading && !enviandoTodos}
            size="small"
            scroll={{ x: 900 }}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{
              emptyText:
                filtroEstado === FILTRO_ESTADO.TODOS
                  ? "No hay consumos registrados en este turno"
                  : "No hay consumos con el filtro seleccionado",
            }}
            rowClassName={(record) =>
              progresoEnvio?.actual?.id === record.id_turno_detalle ? "table-active" : ""
            }
          />
        </div>
      </Modal>

      <Modal
        title={
          <span className="d-flex align-items-center gap-2">
            <MdErrorOutline color="#dc2626" />
            Detalle de error — {errorDetalle?.record?.comensal_nombre || "Consumo"}
          </span>
        }
        open={modalErrorOpen}
        onCancel={() => setModalErrorOpen(false)}
        footer={
          <Button className="btn-dh-secondary" onClick={() => setModalErrorOpen(false)}>
            Cerrar
          </Button>
        }
        width={820}
        destroyOnClose
        className="modal-dh"
      >
        {loadingErrorDetalle ? (
          <div className="text-center py-4">
            <Spin tip="Cargando historial de envío…" />
          </div>
        ) : (
          <>
            <Descriptions bordered size="small" column={2} className="mb-3">
              <Descriptions.Item label="DNI">
                {errorDetalle?.record?.dni ||
                  errorDetalle?.record?.numero_documento_comedor ||
                  "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Intentos">
                {errorDetalle?.sync?.intentos ?? errorDetalle?.record?.intentos ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="Estado sync" span={2}>
                <Tag color="error">{errorDetalle?.sync?.estado_envio || "error"}</Tag>
              </Descriptions.Item>
            </Descriptions>

            {(errorDetalle?.sync?.respuesta_proveedor ||
              errorDetalle?.record?.respuesta_proveedor) && (
              <div className="mb-3">
                <div className="small fw-semibold mb-1">Última respuesta del proveedor</div>
                <div className="log-response-box">
                  {errorDetalle?.sync?.respuesta_proveedor ||
                    errorDetalle?.record?.respuesta_proveedor}
                </div>
              </div>
            )}

            <div className="small fw-semibold mb-2">
              Historial de intentos ({errorDetalle?.logs?.length || 0})
            </div>
            <Table
              rowKey="id"
              size="small"
              columns={logColumns}
              dataSource={errorDetalle?.logs || []}
              pagination={false}
              expandable={{
                expandedRowRender: (log) => (
                  <div className="py-1">
                    {log.request_url && (
                      <div className="mb-2">
                        <div className="small text-muted mb-1">URL de solicitud</div>
                        <div className="log-url-box">{log.request_url}</div>
                      </div>
                    )}
                    {log.response_body && (
                      <div>
                        <div className="small text-muted mb-1">Cuerpo de respuesta</div>
                        <div className="log-response-box">{log.response_body}</div>
                      </div>
                    )}
                  </div>
                ),
                rowExpandable: (log) => Boolean(log.request_url || log.response_body),
              }}
              locale={{ emptyText: "Sin registros en consumos_proveedor_envio_log" }}
            />
          </>
        )}
      </Modal>
    </>
  );
};

export default ModalDetalleConsumosProveedor;
